import { Link, NavLink, useNavigate } from "react-router-dom";
import { LayoutDashboard, Sparkles, Image, MessageSquareText, Settings, LogOut, CreditCard, Wand2, Library, ShieldCheck } from "lucide-react";
import Logo from "@/components/Logo";
import Powered360 from "@/components/Powered360";
import { useAuth } from "@/contexts/AuthContext";

const items = [
    { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testId: "nav-dashboard" },
    { to: "/enhance", label: "AI Enhancer", icon: Sparkles, testId: "nav-enhance" },
    { to: "/background", label: "Backgrounds", icon: Wand2, testId: "nav-background" },
    { to: "/captions", label: "Captions", icon: MessageSquareText, testId: "nav-captions" },
    { to: "/library", label: "Library", icon: Library, testId: "nav-library" },
    { to: "/pricing", label: "Plans", icon: CreditCard, testId: "nav-pricing" },
    { to: "/settings", label: "Settings", icon: Settings, testId: "nav-settings" },
];

export default function Sidebar() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const handleLogout = async () => {
        await logout();
        navigate("/");
    };
    return (
        <aside className="hidden md:flex w-64 shrink-0 flex-col bg-zinc-950 border-r border-white/5 px-4 py-6 sticky top-0 h-screen">
            <div className="mb-8 px-2 space-y-4">
                <Logo size="lg" />
                <Powered360 variant="stack" />
            </div>
            <nav className="flex-1 space-y-1">
                {items.map(({ to, label, icon: Icon, testId }) => (
                    <NavLink
                        key={to}
                        to={to}
                        data-testid={testId}
                        className={({ isActive }) =>
                            `sidebar-item flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-zinc-400 ${isActive ? "active" : ""}`
                        }
                    >
                        <Icon className="h-4 w-4" strokeWidth={2} />
                        <span>{label}</span>
                    </NavLink>
                ))}
                {user?.role === "admin" && (
                    <NavLink
                        to="/admin"
                        data-testid="nav-admin"
                        className={({ isActive }) =>
                            `sidebar-item flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-rose-300/80 ${isActive ? "active" : ""}`
                        }
                    >
                        <ShieldCheck className="h-4 w-4" strokeWidth={2} />
                        <span>Admin</span>
                    </NavLink>
                )}
            </nav>
            <div className="mt-6 pt-6 border-t border-white/5">
                <div className="px-3 py-2 mb-2">
                    <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-zinc-500">
                        {user?.plan === "trial" ? "Free Trial" : user?.plan}
                    </div>
                    <div className="mt-1 text-sm text-white truncate" data-testid="sidebar-user-name">{user?.name}</div>
                    <div className="text-xs text-zinc-500 truncate">{user?.email}</div>
                    <div className="mt-2 text-xs text-orange-400 font-mono" data-testid="sidebar-credits">
                        {user?.credits_remaining ?? 0} credits
                    </div>
                </div>
                <button
                    type="button"
                    onClick={handleLogout}
                    data-testid="sidebar-logout-btn"
                    className="sidebar-item flex w-full items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-zinc-500"
                >
                    <LogOut className="h-4 w-4" />
                    <span>Logout</span>
                </button>
            </div>
        </aside>
    );
}
